import './styles.css'
import { useRef, useState, useCallback, useEffect } from 'react'

const scrollTo = (selector) => {
  const el = document.querySelector(selector)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth' })
}

export default function Overlay() {
  const containerRef = useRef(null)
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)

  // Forward wheel + touch events from text elements so scroll works over text
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let touchStartY = 0

    const handleWheel = (e) => {
      if (e.target.closest('header')) return
      window.scrollBy(0, e.deltaY)
    }

    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY
    }

    const handleTouchMove = (e) => {
      if (e.target.closest('header')) return
      const el = e.target
      const isText = el.closest('.hero-text, .wrapper, .card, .numbers-title, .footer-quote, .footer-tagline, .footer-bottom, .info-stats, .abilities-list')
      if (!isText) return
      const deltaY = touchStartY - e.touches[0].clientY
      touchStartY = e.touches[0].clientY
      window.scrollBy(0, deltaY)
    }

    container.addEventListener('wheel', handleWheel, { passive: true })
    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchmove', handleTouchMove, { passive: true })
    return () => {
      container.removeEventListener('wheel', handleWheel)
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
    }
  }, [])

  const toggleMusic = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    if (!isPlaying) {
      audio.play().then(() => {
        setIsPlaying(true)
      }).catch((err) => {
        console.error('Audio play error:', err)
      })
    } else {
      audio.pause()
      setIsPlaying(false)
    }
  }, [isPlaying])

  return (
    <div className="container" ref={containerRef}>
      <div className="line line1" />
      <div className="line line2" />
      <div className="line line3" />
      <div className="line line4" />
      <div className="line line5" />

      <header>
        <div className="brand" onClick={() => scrollTo('.section-1')}>
          <p>
            SPIDERMAN <strong>BLACK</strong>
          </p>
        </div>

        <div className='header-nav'>
          <ul>
            <li onClick={() => scrollTo('.section-1')}>Symbiote</li>
            <li onClick={() => scrollTo('.section-2')}>Abilities</li>
            <li onClick={() => scrollTo('.section-3')}>Evolution</li>
          </ul>
        </div>

        <button onClick={toggleMusic}>
          {isPlaying ? 'PAUSE' : 'SOUND'}
        </button>
        <audio ref={audioRef} src="/music.mp3" preload="auto" loop />
      </header>

      <div className="main-wrapper">
        <section className="section section-1">
          <div className="hero-text">
            <p className="hero-subtitle">THE ALIEN SUIT</p>
            <h1>EMBRACE THE <strong>DARKNESS</strong></h1>
            <p className="hero-desc">
              A living alien organism from beyond the stars. It chose Peter Parker.
              It made him stronger, faster, deadlier. But every gift has a price.
            </p>
          </div>
          <div className="hero-videos">
            <video width="380" height="280" autoPlay muted loop playsInline>
              <source src="1.mp4" type="video/mp4" />
            </video>
            <video width="380" height="280" autoPlay muted loop playsInline>
              <source src="3.mp4" type="video/mp4" />
            </video>
            <video width="380" height="280" autoPlay muted loop playsInline>
              <source src="2.mp4" type="video/mp4" />
            </video>
          </div>
        </section>

        <section className="section section-2">
          <div className="wrapper">
            <h2>Dark Origin</h2>
            <p>
              On Battleworld, an alien entity bonded with Peter Parker
              and transformed him forever. What seemed like a simple costume
              upgrade was actually a sentient organism — a creature with
              its own hunger, memories, and an insatiable will to survive.
              The symbiote didn't just cover Peter. It became part of him.
            </p>
            <h2>Unleashed Power</h2>
            <p>
              The symbiote erased every limitation. Organic webbing that
              regenerates endlessly. Strength that rivals the Hulk. The ability
              to shift shape, mimic any clothing, and vanish into shadows
              like a phantom. Spider-sense became unnecessary — the suit
              itself could feel danger before it arrived.
            </p>
            <h2>The Inner War</h2>
            <p>
              But the suit was hungry. Every night it hijacked Peter's body,
              pushing him to fight, to hunt, to unleash violence without
              restraint. The rage felt intoxicating. The power felt limitless.
              And that was the trap — the more Peter used the suit,
              the harder it became to take it off.
            </p>

            <div className="info-stats">
              <div className="stat">
                <span className="stat-number">1984</span>
                <span className="stat-label">First Appearance</span>
              </div>
              <div className="stat">
                <span className="stat-number">616</span>
                <span className="stat-label">Universe</span>
              </div>
              <div className="stat">
                <span className="stat-number">7+</span>
                <span className="stat-label">Known Hosts</span>
              </div>
            </div>
          </div>
        </section>

        <section className="section section-3">
          <div className="card-wrapper">
            <div className="card">
              <h2>252</h2>
              <p>The issue where the black suit debuted and changed comics forever.</p>
            </div>
            <div className="card">
              <h2>10X</h2>
              <p>Power amplification — speed, reflexes, healing beyond human limits.</p>
            </div>
            <div className="card">
              <h2>5</h2>
              <p>Symbiote offspring — Venom, Carnage, Toxin, Scream, Anti-Venom.</p>
            </div>
          </div>
          <div className="numbers-title">
            <h2>
              RAW <strong>POWER</strong>
            </h2>
            <p>
              From Peter Parker to Eddie Brock, the symbiote sought hosts
              who matched its intensity. Each bond created something new
              and terrifying. Venom became the lethal protector. Carnage
              became pure chaos. And the lineage continues — an ever-growing
              family tree of alien power that refuses to die.
            </p>
            <div className="abilities-list">
              <span>Shapeshifting</span>
              <span>Organic Webbing</span>
              <span>Camouflage</span>
              <span>Healing Factor</span>
              <span>Wall Crawling</span>
              <span>Superhuman Strength</span>
            </div>
          </div>
        </section>
      </div>

      <footer>
        <div className="footer-content">
          <div className="footer-left">
            <h3>We are not alone</h3>
            <p className="footer-tagline">We are Venom.</p>
          </div>
          <div className="footer-right">
            <p className="footer-quote">
              "The suit doesn't give you power. It reveals who you truly are."
            </p>
            <div className="footer-links">
              <span onClick={() => scrollTo('.section-1')}>Symbiote</span>
              <span onClick={() => scrollTo('.section-2')}>Abilities</span>
              <span onClick={() => scrollTo('.section-3')}>Evolution</span>
              <span onClick={() => scrollTo('.section-1')}>Origins</span>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="copyright">All rights reserved © 2025 by Unfor</p>
          <p className="copyright">SPIDERMAN BLACK — The Symbiote Saga</p>
        </div>
      </footer>
    </div>
  )
}
